import { ComponentInstance, IComponentLifecyclePlugin } from './core/types';

import { StateManagerPlugin } from './plugins/StateManagerPlugin';
import Vue from 'vue';

export class ComponentLifecycleTracker {
  private static instance: ComponentLifecycleTracker;
  private plugins: Map<string, IComponentLifecyclePlugin> = new Map();
  private enabled: boolean = false;
  private componentMap = new Map<string, ComponentInstance>();
  private componentStats = new Map<string, { count: number, instances: ComponentInstance[] }>();
  private componentIdCounter = 0;

  // Component filter config
  private blacklistedComponents: string[] = ['PerformanceDebugger', 'ComponentLifecycleDebugger'];
  private whitelistedComponents: string[] = [];
  private useWhitelist: boolean = false;

  private constructor() {
    // Initialize with empty plugins
  }

  static getInstance(): ComponentLifecycleTracker {
    if (!this.instance) {
      this.instance = new ComponentLifecycleTracker();
      // this.instance.addPlugin(new ConsoleLoggerPlugin());
      this.instance.addPlugin(new StateManagerPlugin() as unknown as IComponentLifecyclePlugin);
    }

    return this.instance;
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  enable(): void {
    if (this.enabled) return;

    this.enabled = true;
    this.setupVueMixin();
    console.log("%c[Component Lifecycle] Tracker Enabled", "color: green; font-weight: bold;");
  }

  disable(): void {
    if (!this.enabled) return;

    this.enabled = false;

    this.plugins.forEach(plugin => {
      if (typeof (plugin as any).disable === 'function') {
        (plugin as any).disable();
      }
    });

    console.log("%c[Component Lifecycle] Tracker Disabled", "color: orange; font-weight: bold;");
  }

  addPlugin(plugin: IComponentLifecyclePlugin): void {
    if (!this.plugins.has(plugin.name)) {
      this.plugins.set(plugin.name, plugin);
      plugin.initialize(this);
      console.log(`[Component Lifecycle] Plugin added: ${plugin.name}`);
    }
  }

  getPlugin(id: string): IComponentLifecyclePlugin | undefined {
    return this.plugins.get(id);
  }

  private shouldTrackComponent(componentName: string): boolean {
    if (!componentName || componentName === 'Anonymous') return false;

    // Skip blacklisted components
    const isBlacklisted = this.blacklistedComponents.some(
      name => componentName.startsWith(name) || componentName.includes(name)
    );

    if (isBlacklisted) return false;

    if (this.useWhitelist) {
      return this.whitelistedComponents.includes(componentName);
    }

    // Skip Vue internal components
    if (componentName.startsWith('Vc') || componentName.startsWith('_')) return false;

    return true;
  }

  private setupVueMixin(): void {
    const tracker = this;

    Vue.mixin({
      beforeCreate() {
        if (!tracker.enabled) return;

        // Get component information
        const filePath = (this.$options as any).__file || '';
        const name = this.$options.name ||
                     filePath.split('/').pop()?.replace('.vue', '') ||
                     'Anonymous';

        // Check if we should track this component
        if (!tracker.shouldTrackComponent(name)) return;

        // Track component creation
        tracker.trackComponentCreation(this, name, filePath);
      },

      destroyed() {
        if (!tracker.enabled) return;

        // Get component information
        const filePath = this.$options.__file || '';
        const name = this.$options.name ||
                     filePath.split('/').pop()?.replace('.vue', '') ||
                     'Anonymous';

        // Check if we should track this component
        if (!tracker.shouldTrackComponent(name)) return;

        // Track component destruction
        tracker.trackComponentDestruction(this, name);
      }
    });
  }

  private trackComponentCreation(vm: any, name: string, filePath: string): void {
    // Create unique ID for component
    const componentId = `comp_${++this.componentIdCounter}`;

    // Find parent ID if available
    let parentId: string | undefined;
    if (vm.$parent && vm.$parent._uid) {
      const parentEntry = Array.from(this.componentMap.values()).find(
        c => c.name === (vm.$parent.$options.name || 'Unknown')
      );
      if (parentEntry) {
        parentId = parentEntry.id;
      }
    }

    // Create component instance record
    const component: ComponentInstance = {
      id: componentId,
      name,
      filePath,
      htmlElement: vm.$el,
      createdAt: Date.now(),
      isMounted: true,
      parentId: parentId
    };

    // Store in component map
    this.componentMap.set(componentId, component);

    // Update component stats
    this.updateComponentStats(name, component, true);

    // Notify plugins
    this.plugins.forEach(plugin => {
      if (plugin && typeof plugin.onComponentCreated === 'function') {
        plugin.onComponentCreated(component);
      }
    });
  }

  private trackComponentDestruction(vm: any, name: string): void {
    // Find component by name and VM instance
    // This approach may need refinement for more accuracy
    const componentEntries = Array.from(this.componentMap.entries())
      .filter(([_, comp]) => comp.name === name);

    // Simple approach: take the first one (may not be accurate if multiple instances)
    const componentEntry = componentEntries[0];
    if (componentEntry) {
      const [componentId, component] = componentEntry;

      // Calculate component lifespan
      component.isMounted = false;
      component.lifespan = Date.now() - component.timestamp;

      // Remove from component map
      this.componentMap.delete(componentId);

      // Update component stats
      this.updateComponentStats(name, component, false);

      // Notify plugins
      this.plugins.forEach(plugin => {
        if (plugin && typeof plugin.onComponentDestroyed === 'function') {
          plugin.onComponentDestroyed(component);
        }
      });
    }
  }

  private updateComponentStats(name: string, component: ComponentInstance, isCreation: boolean): void {
    let stats = this.componentStats.get(name);

    if (!stats) {
      stats = {
        count: 0,
        instances: []
      };
      this.componentStats.set(name, stats);
    }

    if (isCreation) {
      stats.count++;
      stats.instances.push(component);
    } else {
      stats.count = Math.max(0, stats.count - 1);
      stats.instances = stats.instances.filter(c => c.id !== component.id);

      // Remove the component type from stats if count reaches 0
      if (stats.count === 0) {
        this.componentStats.delete(name);
      }
    }
  }

  getComponentMap(): Map<string, ComponentInstance> {
    return this.componentMap;
  }

  getStats(): { totalComponents: number, byType: Array<{ name: string, count: number, instances: ComponentInstance[] }> } {
    return {
      totalComponents: this.componentMap.size,
      byType: Array.from(this.componentStats.entries()).map(([name, stats]) => ({
        name,
        count: stats.count,
        instances: stats.instances
      }))
    };
  }

  dumpDebugInfo(): void {
    console.group("%c[Component Lifecycle] Debug Information", "color: blue; font-weight: bold");
    console.log("Enabled:", this.enabled);
    console.log("Component Count:", this.componentMap.size);
    console.log("Plugins:", Array.from(this.plugins.keys()));
    console.groupEnd();

    // Let plugins output their debug info
    this.plugins.forEach(plugin => {
      if (plugin && typeof plugin.dumpDebugInfo === 'function') {
        plugin.dumpDebugInfo();
      }
    });
  }
}

